const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function toEventTimestamp(value) {
  if (value == null || value === "") return 0;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return 0;
    return value > 0 && value < 1e12 ? value * 1000 : value;
  }
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value.getTime() : 0;
  if (typeof value === "object") {
    if (typeof value.toMillis === "function") return Number(value.toMillis()) || 0;
    if (Number.isFinite(value.seconds)) {
      return Number(value.seconds) * 1000 + Math.round((Number(value.nanoseconds) || 0) / 1e6);
    }
  }
  const text = String(value).trim();
  const dateOnly = text.match(DATE_ONLY_RE);
  if (dateOnly) {
    const [, year, month, day] = dateOnly;
    const local = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isFinite(local.getTime()) ? local.getTime() : 0;
  }
  const parsed = Date.parse(text);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function endOfLocalDay(timestamp) {
  if (!Number.isFinite(timestamp) || timestamp <= 0) return 0;
  const date = new Date(timestamp);
  date.setHours(23, 59, 59, 999);
  return date.getTime();
}

export function getEventStartTimestamp(event) {
  return toEventTimestamp(event?.startAt || event?.date);
}

export function getEventEndTimestamp(event) {
  if (!event) return 0;

  const dayEnds = (Array.isArray(event.meetingPoints) ? event.meetingPoints : [])
    .map((day) => toEventTimestamp(day?.date || day?.startAt))
    .filter((timestamp) => timestamp > 0)
    .map(endOfLocalDay);
  if (dayEnds.length) return Math.max(...dayEnds);

  const explicitValue = event.endAt || event.endDate;
  if (explicitValue) {
    const timestamp = toEventTimestamp(explicitValue);
    if (!timestamp) return 0;
    return typeof explicitValue === "string" && DATE_ONLY_RE.test(explicitValue.trim())
      ? endOfLocalDay(timestamp)
      : timestamp;
  }

  return endOfLocalDay(getEventStartTimestamp(event));
}

export function getEventCalendarDayCount(event) {
  const dayKeys = (Array.isArray(event?.meetingPoints) ? event.meetingPoints : [])
    .map((day) => String(day?.date || "").slice(0, 10))
    .filter((value) => DATE_ONLY_RE.test(value));
  if (dayKeys.length) return Math.max(new Set(dayKeys).size, 1);

  const startTimestamp = getEventStartTimestamp(event);
  const endTimestamp = getEventEndTimestamp(event);
  if (!startTimestamp || !endTimestamp || endTimestamp < startTimestamp) return 1;

  const start = new Date(startTimestamp);
  const end = new Date(endTimestamp);
  const startDay = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
  const endDay = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.max(1, Math.round((endDay - startDay) / 86400000) + 1);
}

export function isEventPast(event, now = Date.now()) {
  const endTimestamp = getEventEndTimestamp(event);
  return endTimestamp > 0 && endTimestamp < now;
}
